import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  DefaultButton,
  ScrollablePane,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { SectionTitle } from "@components/SectionTitle";
import { SoftwarePackageVersion } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useSoftwarePackageVersions } from "@libs/api/gateways/field/field-ops-gateway.hooks";

import { SoftwarePackageDetailsCard } from "./SoftwarePackageDetailsCard";
import { SoftwarePackageVersionsTable } from "./SoftwarePackageVersionsTable";
import {
  SoftwarePackageVersionTableFilter,
  SoftwarePackageVersionTableFilterValues
} from "./SoftwarePackageVersionTableFilter";

export const SoftwarePackageVersionsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { softwarePackageId } = useParams<{ softwarePackageId: string }>();
  const [filtersArgs, setFilterArgs] = useState<
    SoftwarePackageVersionTableFilterValues
  >({
    version: ""
  });

  const softwarePackageVersionsQuery = useSoftwarePackageVersions({
    softwarePackageId: softwarePackageId ? softwarePackageId : undefined
  });

  return (
    <Stack
      verticalFill
      styles={{
        root: { padding: theme.spacing.s1, width: "100%" }
      }}
      tokens={{ childrenGap: theme.spacing.m }}
    >
      <QueryStateIndicator {...softwarePackageVersionsQuery}>
        {softwarePackageVersions => {
          const softwarePackageName =
            softwarePackageVersions?.[0]?.softwarePackageDisplayName ??
            "Unknown Software Package";

          return (
            <>
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: theme.spacing.l1 }}
              >
                <DefaultButton onClick={() => navigate("..")}>
                  Back to Software Packages
                </DefaultButton>

                <SectionTitle>{softwarePackageName}</SectionTitle>
              </Stack>

              <SoftwarePackageDetailsCard
                softwarePackageVersion={softwarePackageVersions?.[0]}
              />

              <Text
                variant="xLarge"
                styles={{ root: { color: theme.palette.themePrimary } }}
              >
                Versions
              </Text>

              <SoftwarePackageVersionTableFilter
                setFilterArgs={setFilterArgs}
              />
              <ScrollablePane
                styles={{
                  root: {
                    height: "90%",
                    position: "relative"
                  }
                }}
              >
                <SoftwarePackageVersionsTable
                  items={filterAndSortRolloutSoftwarePackageVersions(
                    softwarePackageVersions ?? [],
                    filtersArgs
                  )}
                />
              </ScrollablePane>
            </>
          );
        }}
      </QueryStateIndicator>
    </Stack>
  );
};

const filterAndSortRolloutSoftwarePackageVersions = (
  rolloutSoftwarePackageVersions: SoftwarePackageVersion[],
  filter: SoftwarePackageVersionTableFilterValues
): SoftwarePackageVersion[] => {
  return rolloutSoftwarePackageVersions
    .filter(
      d =>
        filter.version === "" ||
        d.version.toLowerCase().indexOf(filter.version.toLowerCase()) >= 0
    )
    .sort((a: SoftwarePackageVersion, b: SoftwarePackageVersion) => {
      return a.version < b.version ? 1 : -1;
    });
};
